import { round } from '@deltasierra/utilities-math';
import { isNotNullOrUndefined, isNullOrUndefined } from '@deltasierra/type-utilities';

export type Axis = 'x' | 'y';

export interface IPoint {
    x: number;
    y: number;
}

export enum HandleType {
    resize = 'resize',
    rotate = 'rotate',
}

export interface IHandle extends IPoint {
    positionName: RectanglePoint;
    type: HandleType;
}

export interface ISize {
    width: number;
    height: number;
}

export interface IRectangle {
    x: number;
    y: number;
    width: number;
    height: number;
}

export interface IEllipse {
    x: number;
    y: number;
    radiusX: number;
    radiusY: number;
    rotation: number;
    startAngle: number;
    endAngle: number;
    anticlockwise?: boolean;
}

export enum RectangleCorner {
    topLeft = 'topLeft',
    topRight = 'topRight',
    bottomRight = 'bottomRight',
    bottomLeft = 'bottomLeft',
}

export enum RectanglePoint {
    top = 'top',
    bottom = 'bottom',
    left = 'left',
    right = 'right',
    topLeft = 'topLeft',
    topRight = 'topRight',
    bottomRight = 'bottomRight',
    bottomLeft = 'bottomLeft',
}

export function isRectangle(value: any): value is IRectangle {
    return typeof value === 'object' && isPoint(value) && isSize(value);
}

export function isPoint(value: any): value is IPoint {
    return typeof value === 'object' && value.x !== undefined && value.y !== undefined;
}

export function isSize(value: any): value is ISize {
    return typeof value === 'object' && value.width !== undefined && value.height !== undefined;
}

export function isEllipse(value: any): value is IEllipse {
    return typeof value === 'object' && value.radiusX !== undefined && value.radiusY !== undefined;
}

/**
 * Accepts any number of rectangles and returns a new smallest rectangle that contains all
 *
 * @param inRectangles - A list of rectangles
 * @returns The containing rectangle
 */
export function getRectangleContaining(...inRectangles: Array<IRectangle | null>): IRectangle | null {
    const rectangles: IRectangle[] = inRectangles.filter(x => !!x) as IRectangle[];
    if (!rectangles.length) {
        return null;
    }

    if (rectangles.length === 1) {
        return {
            x: rectangles[0].x,
            y: rectangles[0].y,
            width: rectangles[0].width,
            height: rectangles[0].height,
        };
    }

    const minX = Math.min(...rectangles.map(rect => rect.x));
    const minY = Math.min(...rectangles.map(rect => rect.y));
    const maxX = Math.max(...rectangles.map(rect => rect.x + rect.width));
    const maxY = Math.max(...rectangles.map(rect => rect.y + rect.height));

    return {
        x: minX,
        y: minY,
        width: maxX - minX,
        height: maxY - minY,
    };
}

export function getRectangleContainingFromCenter(...rectangles: IRectangle[]): IRectangle | null {
    const localRects = rectangles.map(rect => {
        const newPosition = offsetPoints(
            {
                x: -(rect.width / 2),
                y: -(rect.height / 2),
            },
            rect,
        );
        return {
            x: newPosition[0].x,
            y: newPosition[0].y,
            width: rect.width,
            height: rect.height,
        };
    });

    const containingRect = getRectangleContaining(...localRects);

    if (!containingRect) {
        return null;
    }

    const newContainingPosition = offsetPoints(
        {
            x: containingRect.width / 2,
            y: containingRect.height / 2,
        },
        containingRect,
    );
    return {
        x: newContainingPosition[0].x,
        y: newContainingPosition[0].y,
        width: containingRect.width,
        height: containingRect.height,
    };
}

/**
 * Returns a rectangle the contains all points position returned is center of rectangle.
 *
 * @param points - A list of points
 * @returns A rectangle containing all the points
 */
export function getRectangleContainingPoints(...points: IPoint[]): IRectangle | null {
    // eslint-disable-next-line no-param-reassign
    points = points.filter(x => !!x);
    if (!points.length) {
        return null;
    }
    const xMap = points.map(point => point.x);
    const yMap = points.map(point => point.y);

    const minX = Math.min(...xMap);
    const minY = Math.min(...yMap);
    const maxX = Math.max(...xMap);
    const maxY = Math.max(...yMap);

    const width = maxX - minX;
    const height = maxY - minY;

    return {
        x: minX + width / 2,
        y: minY + height / 2,
        width,
        height,
    };
}

export function isWithinAspectRatioTolerance(
    aspectRatio: number,
    targetAspectRatio: number,
    tolerance = 0.01,
): boolean {
    const result = Math.abs(aspectRatio - targetAspectRatio) <= tolerance;
    return result;
}

export function scale(value: IPoint, scaleAmount: IPoint | number): IPoint;
export function scale(value: ISize, scaleAmount: IPoint | number): ISize;
export function scale(value: IRectangle, scaleAmount: IPoint | number): IRectangle;
export function scale(value: number, scaleAmount: number): number;

export function scale(
    value: IPoint | IRectangle | ISize | number,
    scaleAmount: IPoint | number,
): IPoint | IRectangle | ISize | number {
    const scaleX = typeof scaleAmount === 'object' ? scaleAmount && scaleAmount.x : scaleAmount;

    const scaleY = typeof scaleAmount === 'object' ? scaleAmount && scaleAmount.y : scaleAmount;

    if (isRectangle(value)) {
        return {
            x: value.x * scaleX,
            y: value.y * scaleY,
            width: value.width * scaleX,
            height: value.height * scaleY,
        };
    } else if (isSize(value)) {
        return {
            width: value.width * scaleX,
            height: value.height * scaleY,
        };
    } else if (isPoint(value)) {
        return {
            width: value.x * scaleX,
            height: value.y * scaleY,
        };
    } else if (typeof value === 'number') {
        return value * scaleX;
    } else {
        throw new Error(`Cannot apply scale to value: ${value}`);
    }
}

export function inflate(rect: IRectangle, amount: IPoint | number): IRectangle {
    const inflateX = typeof amount === 'number' ? amount : amount.x;
    const inflateY = typeof amount === 'number' ? amount : amount.y;

    return {
        x: rect.x + inflateX,
        y: rect.y + inflateY,
        width: rect.width - inflateX * 2,
        height: rect.height - inflateY * 2,
    };
}

export function getCenter(value: IRectangle | ISize, options?: { round?: boolean }): IPoint {
    // eslint-disable-next-line no-param-reassign
    options ||= { round: false };
    options.round = options.round === undefined ? false : options.round;

    const result: IPoint = {
        x: value.width / 2,
        y: value.height / 2,
    };

    if (isRectangle(value)) {
        result.x += value.x;
        result.y += value.y;
    }

    return result;
}

export function addPoints(...points: IPoint[]): IPoint {
    const result: IPoint = { x: 0, y: 0 };
    for (const point of points) {
        result.x += point.x;
        result.y += point.y;
    }
    return result;
}

export interface TransformSizeOptions {
    skewX?: number;
    skewY?: number;
    zoom?: number;
}

export function getBoundarySize(rect: ISize, options: TransformSizeOptions): ISize {
    let width = Math.abs(rect.width);
    let height = Math.abs(rect.height);

    // Skew
    if (isNotNullOrUndefined(options.skewX)) {
        width += Math.abs(rect.height) * Math.tan(Math.atan(Math.abs(options.skewX)));
    }
    if (isNotNullOrUndefined(options.skewY)) {
        height += Math.abs(rect.width) * Math.tan(Math.atan(Math.abs(options.skewY)));
    }

    // Zoom
    if (isNotNullOrUndefined(options.zoom)) {
        width *= options.zoom;
        height *= options.zoom;
    }

    return { width, height };
}

export interface TranslatePointOptions {
    zoom?: number;
    panX?: number;
    panY?: number;
}

/*
 * Give 2 rectangles, translate a point on rectangle A to Rectange B based on translation options
 * This assumes that the point is relative to the center
 */
export function translatePoint(point: IPoint, from: ISize, translations: TranslatePointOptions, to: ISize): IPoint {
    translations.zoom ||= 1;
    if (isNullOrUndefined(translations.panX)) {
        translations.panX = 1;
    }
    if (isNullOrUndefined(translations.panY)) {
        translations.panY = 1;
    }

    const center = getCenter(to);
    const fromScaled = {
        width: from.width * translations.zoom,
        height: from.height * translations.zoom,
    };

    const panHorizontal = fromScaled.width > to.width ? translations.panX : 1;
    const panVertical = fromScaled.height > to.height ? translations.panY : 1;

    // Translated position
    const toX =
        center.x -
        (fromScaled.width / 2) * panHorizontal +
        fromScaled.width / 2 +
        Math.floor(point.x) * translations.zoom;
    const toY =
        center.y -
        (fromScaled.height / 2) * panVertical +
        fromScaled.height / 2 +
        Math.floor(point.y) * translations.zoom;

    return { x: toX, y: toY };
}

export function translatePoints(
    points: IPoint[],
    from: ISize,
    translations: TranslatePointOptions,
    to: ISize,
): IPoint[] {
    const translatedPoints = points.map(point => translatePoint(point, from, translations, to));

    return translatedPoints;
}

export function rotateFromCenter(point: IPoint, center: IPoint, rotation: number, counterClockwise = false): IPoint {
    if (point.x === center.x && point.y === center.y) {
        return point;
    }

    // eslint-disable-next-line no-param-reassign
    rotation = counterClockwise ? rotation : -rotation;
    const radians = (Math.PI / 180) * rotation;

    const cos = Math.cos(radians);
    const sin = Math.sin(radians);

    const newX = cos * (point.x - center.x) + sin * (point.y - center.y) + center.x;
    const newY = cos * (point.y - center.y) - sin * (point.x - center.x) + center.y;

    return { x: newX, y: newY };
}

export function getDistance(pointA: IPoint, pointB: IPoint): number {
    // eslint-disable-next-line id-length
    const a = pointA.x - pointB.x;
    // eslint-disable-next-line id-length
    const b = pointA.y - pointB.y;

    const distance = Math.sqrt(a * a + b * b);

    return distance;
}

export function offsetPoints(offset: IPoint, ...points: IPoint[]): IPoint[] {
    const pointsOffset = points.map(point => {
        point.x += offset.x;
        point.y += offset.y;
        return point;
    });

    return pointsOffset;
}

export function isPointWithin(point: IPoint, rect: IRectangle, tolerance: IPoint | number): boolean {
    const toleranceX = typeof tolerance === 'number' ? tolerance : tolerance.x;
    const toleranceY = typeof tolerance === 'number' ? tolerance : tolerance.y;

    const topLeft = {
        x: Math.floor(rect.x - rect.width / 2 - toleranceX),
        y: Math.floor(rect.y - rect.height / 2 - toleranceY),
    };
    const bottomLeft = {
        x: Math.ceil(rect.x + rect.width / 2 + toleranceX),
        y: Math.ceil(rect.y + rect.height / 2 + toleranceY),
    };

    return point.x > topLeft.x && point.x < bottomLeft.x && point.y > topLeft.y && point.y < bottomLeft.y;
}

export function swapWidthAndHeight(size: ISize): ISize {
    return {
        width: size.height,
        height: size.width,
    };
}

export function getCornerPointsFromCenter(rect: ISize): IPoint[] {
    const corners = [
        { x: -(rect.width / 2), y: -(rect.height / 2) }, // Top Left
        { x: rect.width / 2, y: -(rect.height / 2) }, // Top Right
        { x: rect.width / 2, y: rect.height / 2 }, // Bottom Right
        { x: -(rect.width / 2), y: rect.height / 2 }, // Bottom Left
    ];

    return corners;
}

export function scaleToFill(input: ISize, output: ISize): ISize {
    let scaleAmount = output.width / input.width;
    if (input.height * scaleAmount < output.height) {
        scaleAmount = output.height / input.height;
    }

    const scaled = { width: round(input.width * scaleAmount), height: round(input.height * scaleAmount) };

    return scaled;
}
